import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../config/config'
import { logger } from '../utils'
import * as Sentry from '@sentry/browser'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'

const MAX_RETRIES = 3
const RETRY_STATUS_CODES = [408, 500, 502, 503, 504]
const RETRY_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND', 'ERR_INTERNET_DISCONNECTED', 'ERR_CONNECTION_RESET', 'INTERNET_DISCONNECTED']

const retryDelay = (retryCount: number): number => Math.min(1000 * (2 ** retryCount), 10000)

const axiosInstance = axios.create({
  baseURL: config.baseUrl,
  timeout: 8000,
  headers: {
    Accept: 'application/json',
    Account: config.getAccountSlug()
  }
})

// Add retry logic interceptor
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config

    // Skip retry if explicitly disabled (for testing)
    if (config?.headers?.['x-retry'] === 'false') {
      return await Promise.reject(error)
    }

    // Initialize retry count
    if (config === undefined || config === null || config.retry === undefined) {
      if (config !== undefined && config !== null) {
        config.retry = MAX_RETRIES
        config.__retryCount = 0
      } else {
        return await Promise.reject(error)
      }
    }

    // Check if we should retry
    if (config.__retryCount !== undefined && config.__retryCount !== null && config.__retryCount >= config.retry) {
      return await Promise.reject(error)
    }

    const status = error.response ? error.response.status : null
    const code = error.code

    const isNetworkError = error.response === undefined || error.response === null
    const isRetryableCode = code !== undefined && code !== null && RETRY_ERROR_CODES.includes(code)
    const isRetryableStatus = status !== null && RETRY_STATUS_CODES.includes(status)

    if (isNetworkError || isRetryableCode || isRetryableStatus) {
      if (config.__retryCount !== undefined) {
        config.__retryCount = (config.__retryCount as number) + 1
      } else {
        config.__retryCount = 1
      }

      const reason = isNetworkError
        ? 'no response (network error)'
        : isRetryableCode
          ? `error code: ${String(code)}`
          : `HTTP status: ${String(status)}`

      const logMessage = `Retrying ${String(config.url)} (attempt ${String(config.__retryCount)}/${String(config.retry)}) due to ${reason}`
      Sentry.captureMessage(logMessage, 'info')

      const delay = retryDelay(config.__retryCount)
      return await new Promise(resolve =>
        setTimeout(() => resolve(axiosInstance(config)), delay)
      )
    }

    return await Promise.reject(error)
  }
)

// Add authentication interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    const tokens = await cognitoUserPoolsTokenProvider.getTokens()
    const accessToken = tokens?.accessToken?.toString()

    if (accessToken !== null && accessToken !== undefined) {
      const token = accessToken.toString()
      config.headers.Authorization = `bearer ${token}`
    }
    return config
  },
  async (error) => {
    return await Promise.reject(error)
  }
)

// Add error handling interceptor (runs after retry logic)
axiosInstance.interceptors.response.use(
  (res) => {
    return res
  },
  async (err) => {
    // If we've already retried MAX_RETRIES times and still have an error, handle it
    if (err.response !== null && err.response !== undefined) {
      // Access Token was expired
      if (err.response.status === 401) {
        window.location.href = '/'
      } else {
        toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
      }
      // Return the response so that the UI can manage any custom error handling.
      return err.response
    } else {
      toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
    }
    logger.error(err)
  }
)

export default axiosInstance
